'use client';

import { IGroupInfo } from '@/api/types/product';
import ExpandableText from '@/components/ExpandableText';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import Popup from '@/components/Popup';
import Tooltip from '@/components/Tooltip';
import { getHtmlText } from '@/utils/goods-list-utils';
import { setSession } from '@/utils/tools';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import { useSpmDomAttr, useSpmModuleCode } from '@/reporting/spm';
import { QuestionOutlined } from '@zeelool/icon';
import { cva } from 'class-variance-authority';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { goodsListBannerImageLoader } from '../utils/banner-image';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper, SwiperSlide } from 'swiper/react';

// Banner文本覆盖层样式
const bannerTextVariants = cva('banner-text', {
  variants: {
    platform: {
      web: 'p-40 max-w-[704px] flex-1 flex flex-col justify-center relative',
      mobile: 'absolute left-0 px-14 pb-[15px] w-full bottom-0',
    },
  },
});

interface ProductGroupLinkProps {
  item: any;
  index: number;
  isActive: boolean;
  enterPageScene: string;
  onRelatedGroupExp?: (item: any, index: number) => void;
}

// 产品组链接组件
const ProductGroupLink = ({
  item,
  index,
  isActive,
  enterPageScene,
  onRelatedGroupExp,
}: ProductGroupLinkProps) => {
  const t = useTranslations('goodsList.sortFilterBar');
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  useEffect(() => {
    if (inView) {
      onRelatedGroupExp?.(item, index);
    }
  }, [inView]);

  // if (!item.new_in_date) return null;

  return (
    <Link
      ref={ref}
      key={item.id}
      href={`/goods-list/${item.path || item.id}`}
      className={`
        second-nav-li-a h-[32px] rounded-[20px] text-12 px-18 flex items-center justify-center border-1
        ${
          isActive
            ? 'active bg-primary-text text-white hover:bg-primary-black-hover'
            : 'border-[#808080] bg-white hover:bg-gray-50'
        }`}
      onClick={() => setSession('zl_goodsListEnterPageScene', enterPageScene)}
    >
      {(item.new_in_text ?? '').toLowerCase() === 'new in' ? t('newIn') : item.new_in_text}{' '}
      {item.transverse_new_in_date}
    </Link>
  );
};

interface RelatedLinkProps {
  item: any;
  index: number;
  isWeb: boolean;
  onRelatedGroupClick: (item: any, index: number) => void;
  onRelatedGroupExp: (item: any, index: number) => void;
}

/**
 * 相关链接组件
 * 配置步骤：魔方/商品分组/SEO信息/Banner下区域/分组链接
 */
const RelatedLink = ({
  item,
  index,
  isWeb,
  onRelatedGroupClick,
  onRelatedGroupExp,
}: RelatedLinkProps) => {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  useEffect(() => {
    if (inView) {
      onRelatedGroupExp(item, index);
    }
  }, [inView]);

  return (
    <Link
      ref={ref}
      key={item.path || item.group_link}
      className={`link-item flex items-center bg-white border whitespace-nowrap cursor-pointer hover:bg-gray-50 ${
        isWeb
          ? 'h-[32px] px-[18px] text-[12px] leading-[30px] border-[#d4d4d4] rounded-[20px]'
          : 'h-[30px] px-[10px] text-[11px] leading-[30px] border-[#282828] rounded-[16px]'
      }`}
      href={`/goods-list/${item.path || item.group_link}`}
      onClick={() => {
        setSession('zl_goodsListEnterPageScene', '商品列表页-banner下相关链接');
        onRelatedGroupClick(item, index);
      }}
    >
      {item.link_name}
    </Link>
  );
};

interface GroupBannerProps {
  groupInfo: IGroupInfo;
  onBannerClick?: (params: any) => void;
  onBannerExp?: (params: any) => void;
  onRelatedGroupClick?: (item: any, index: number) => void;
  onRelatedGroupExp?: (params: any, index: number) => void;
  isWeb?: boolean;
}

export default function GroupBanner({
  groupInfo,
  onBannerClick,
  onBannerExp,
  onRelatedGroupClick = () => {},
  onRelatedGroupExp = () => {},
  isWeb = false,
}: GroupBannerProps) {
  const [showPop, setShowPop] = useState(false);
  const toJumpTarget = useJump();

  // Swiper配置
  const swiperOptions = useMemo(() => {
    // 移动端配置：可滑动轮播
    return {
      slidesPerView: 'auto' as const,
      spaceBetween: 10,
      freeMode: true,
      slidesOffsetBefore: 14,
      slidesOffsetAfter: 14,
    };
  }, [isWeb]);

  const group = groupInfo as any; // 临时类型断言

  const bannerInfo = useMemo(() => group?.banner_info || {}, [groupInfo]);
  const bannerDownInfo = useMemo(() => group?.banner_down_info || {}, [groupInfo]);
  const productGroupSort = useMemo(() => {
    const { page_template: { product_group_sort: _productGroupSort = [] } = {} } = groupInfo;
    if (!_productGroupSort || _productGroupSort.length === 0) {
      return [];
    }
    return _productGroupSort.filter((item: any) => item.is_show);
  }, [groupInfo]);

  // 主banner的ref
  const [groupBannerRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  const bannerExposureReported = useRef(false);

  // 顶部 Banner 的真实曝光或点击消费者使用 collection_banner C 位。
  // 介绍和展开描述没有消费对应 SPM 的事件。
  const getSpmModuleCode = useSpmModuleCode();
  const collectionBannerDomSpm = useSpmDomAttr(
    onBannerExp || (onBannerClick && (!isWeb || (bannerInfo.jump_type && bannerInfo.jump_url)))
      ? getSpmModuleCode('collection_banner')
      : undefined,
  );

  // 仅统计图片广告位；回调或数据更新不重复上报已曝光的 Banner。
  useEffect(() => {
    if (inView && bannerInfo.image && onBannerExp && !bannerExposureReported.current) {
      bannerExposureReported.current = true;
      onBannerExp(bannerInfo);
    }
  }, [inView, bannerInfo, onBannerExp]);

  // 处理banner点击
  const handleBannerClick = () => {
    onBannerClick?.(bannerInfo);
    setCampaignEntrancePageScene('列表页广告位', bannerInfo.jump_url);
    const bannerSceneName = '商品列表页banner位';

    if (bannerInfo.jump_type === 1) {
      // 设置商品列表页banner跳转位
      setSession('zl_goodsListEnterPageScene', '商品列表页banner位');
    }
    if (bannerInfo.jump_type === 2) {
      setSession('zl_goodsDetailEnterPageScene', bannerSceneName);
    }
    // 这里可以添加跳转逻辑
    if (bannerInfo.jump_type && bannerInfo.jump_url) {
      const { product_group } = bannerInfo;
      const bannerJumpInfo = bannerInfo.banner_jump_info || {
        jump_type: bannerInfo.jump_type,
        Jump_target: product_group?.path || product_group?.id || bannerInfo.jump_url,
      };
      toJumpTarget(bannerJumpInfo);
    }
  };

  const descWebMaxWidth = 'max-w-[750px]';

  return (
    <div>
      {/* 普通顶部banner */}
      <div className={`goods-list-banner relative ${isWeb && bannerInfo.image ? 'pt-16' : ''}`}>
        {bannerInfo.image && (
          <div
            ref={groupBannerRef}
            className={`banner relative ${isWeb ? 'rounded-12 overflow-hidden flex flex-nowrap flex-row items-center' : ''} ${bannerInfo.jump_type && bannerInfo.jump_url ? 'cursor-pointer' : ''}`}
            style={
              isWeb
                ? {
                    aspectRatio: group.extend_web?.pc_new_img_ratio,
                  }
                : undefined
            }
            onClick={
              isWeb && bannerInfo.jump_type && bannerInfo.jump_url ? handleBannerClick : undefined
            }
            {...collectionBannerDomSpm}
          >
            {isWeb ? (
              <Image
                src={bannerInfo.image}
                alt={group.keywords || 'trendy glasses'}
                fill
                sizes="(min-width: 1280px) 1200px, 100vw"
                className="object-cover object-center"
                loader={goodsListBannerImageLoader}
                quality={100}
                priority
                fetchPriority="high"
                loading="eager"
              />
            ) : (
              <Image
                src={bannerInfo.image}
                alt={group.keywords || 'trendy glasses'}
                className={`block w-full h-auto ${bannerInfo.jump_type && bannerInfo.jump_url ? 'cursor-pointer' : ''}`}
                style={{
                  aspectRatio: group.extend_web?.wap_new_img_ratio,
                }}
                onClick={handleBannerClick}
                fetchPriority="high"
                width={375}
                height={375}
                loader={goodsListBannerImageLoader}
                loading="eager"
                quality={100}
              />
            )}

            {/* Banner文本覆盖层 */}
            {(bannerInfo.banner_title || bannerInfo.banner_desc) && (
              <div
                className={bannerTextVariants({ platform: isWeb ? 'web' : 'mobile' })}
                style={{ color: bannerInfo.banner_text_color }}
              >
                {bannerInfo.banner_title && (
                  <h2
                    className={`title font-bb ${isWeb ? 'text-32 leading-[39px]' : 'text-[16px] leading-[19px]'}`}
                    dangerouslySetInnerHTML={{ __html: getHtmlText(bannerInfo.banner_title) }}
                  />
                )}
                {bannerInfo.banner_desc && (
                  <h3 className={`content-wrapper ${isWeb ? 'mt-12' : 'mt-[6px]'}`}>
                    {isWeb ? (
                      <div
                        className="text-14 leading-[20px]"
                        dangerouslySetInnerHTML={{ __html: getHtmlText(bannerInfo.banner_desc) }}
                      />
                    ) : (
                      <ExpandableText
                        text={bannerInfo.banner_desc}
                        maxLines={1}
                        className="text-12 leading-[18px]"
                      />
                    )}
                  </h3>
                )}
              </div>
            )}
          </div>
        )}

        {/* 条款条件 */}
        {group.rule_pc === 1 && group.rule_content && isWeb ? (
          <div className="absolute left-0 bottom-0">
            <Tooltip
              title={<div dangerouslySetInnerHTML={{ __html: getHtmlText(group.rule_content) }} />}
              placement="top-start"
              trigger="hover"
              variant="dark"
              zIndex={500}
              maxWidth={520}
              backgroundColor="rgba(0,0,0,0.6)"
              sx={{
                '& .MuiTooltip-tooltip': {
                  marginLeft: '0 !important',
                },
              }}
            >
              <div
                className={`
           terms-conditions  bg-[rgba(0,0,0,0.6)] cursor-pointer  text-white  w-[160px] h-[30px] rounded-bl-[14px] rounded-tr-[14px] flex items-center justify-center`}
              >
                <div className={`flex items-center text-12`}>
                  {group.rule_title}

                  <QuestionOutlined className="ml-[4px] w-[12px] inline-block " />
                </div>
              </div>
            </Tooltip>
          </div>
        ) : null}
        {group.rule_wap === 1 && group.rule_content && !isWeb ? (
          <div
            className={`terms-conditions absolute bg-[rgba(0,0,0,0.6)] text-white top-0 right-0 py-[4px] pr-[6px] pl-[8px] rounded-bl-[10px]`}
          >
            <div className={`flex items-center text-[10px]`} onClick={() => setShowPop(true)}>
              {group.rule_title}
              <QuestionOutlined className="ml-[4px] w-[10px] inline-block" />
            </div>
          </div>
        ) : null}
      </div>

      {isWeb && productGroupSort.length > 0 && (
        <ul className="mt-20 pb-20 flex flex-wrap gap-x-16 gap-y-10 border-b border-[#eeeeee]">
          {productGroupSort.map((item: any, index: number) => (
            <ProductGroupLink
              key={item.id}
              item={item}
              index={index}
              isActive={groupInfo.id === item.id}
              enterPageScene={
                groupInfo.page_template?.id === 6
                  ? '商品列表页框型导航分组导航位'
                  : '商品列表页新品分组导航位'
              }
            />
          ))}
        </ul>
      )}
      {/* Banner图下方的独立标题和描述 */}
      {(bannerDownInfo.title ||
        bannerDownInfo.desc ||
        (bannerDownInfo.related_link_list && bannerDownInfo.related_link_list.length > 0)) && (
        <div className={`banner-down-area ${isWeb ? 'mt-24' : 'mt-[16px]'}`}>
          {bannerDownInfo.title && (
            <h2
              className={`banner-title font-bb text-[#282828] ${isWeb ? 'text-32 leading-[39px]' : 'text-[16px] leading-[19px] px-[14px]'}`}
              dangerouslySetInnerHTML={{ __html: getHtmlText(bannerDownInfo.title) }}
            />
          )}

          {bannerDownInfo.desc && (
            <h3
              className={`banner-desc-wrapper text-[#282828] ${isWeb ? `mt-12 ${descWebMaxWidth}` : 'mt-[6px] px-[14px]'}`}
            >
              <ExpandableText
                text={bannerDownInfo.desc}
                maxLines={1}
                className={`${isWeb ? 'text-14 leading-[22px]' : 'text-12 leading-[18px]'}`}
                isWeb={isWeb}
                textClassName={descWebMaxWidth}
              />
            </h3>
          )}

          {/* 相关链接列表 */}
          {bannerDownInfo.related_link_list && bannerDownInfo.related_link_list.length > 0 && (
            <div className={`links-list ${isWeb ? 'mt-24' : 'mt-[16px] mb-[20px]'}`}>
              {isWeb ? (
                // Web端：使用flex-wrap布局，不需要轮播
                <div className="flex gap-x-16 gap-y-12 flex-wrap">
                  {bannerDownInfo.related_link_list.map((item: any, index: number) => (
                    <RelatedLink
                      key={item.path || item.group_link}
                      item={item}
                      index={index}
                      isWeb={isWeb}
                      onRelatedGroupClick={onRelatedGroupClick}
                      onRelatedGroupExp={onRelatedGroupExp}
                    />
                  ))}
                </div>
              ) : (
                // 移动端：使用Swiper轮播
                <div>
                  <Swiper {...swiperOptions}>
                    {bannerDownInfo.related_link_list.map((item: any, index: number) => (
                      <SwiperSlide key={item.path || item.group_link} className="!w-auto">
                        <RelatedLink
                          item={item}
                          index={index}
                          isWeb={isWeb}
                          onRelatedGroupClick={onRelatedGroupClick}
                          onRelatedGroupExp={onRelatedGroupExp}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 弹窗 */}
      {!isWeb && (
        <Popup
          open={showPop}
          onClose={() => setShowPop(false)}
          position="bottom"
          title={group.rule_title}
          height="50vh"
        >
          <div
            className="text-[13px] leading-[20px] pb-40"
            dangerouslySetInnerHTML={{ __html: getHtmlText(group.rule_content) }}
          />
        </Popup>
      )}
    </div>
  );
}
